export const MARKET_COOKIE_NAME = 'storefrontMarket';
export const GEO_COUNTRY_COOKIE_NAME = 'storefrontGeoCountry';
export const MARKET_COUNTRY_HEADER = 'x-storefront-country';
export const MARKET_LANGUAGE_HEADER = 'x-storefront-language';
export const MARKET_LOCALE_HEADER = 'x-storefront-locale';
export const MARKET_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
export const GEO_COUNTRY_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24;
export const MARKET_QUERY_PARAM = 'market';
export const INTERNAL_MARKET_PATH_PREFIX = '/storefront-market';

export interface StorefrontMarket {
	language: string;
	country: string;
	languageCode: string;
	countryCode: string;
	locale: string;
	currencyCode: string;
	label: string;
}

const MARKET_SEGMENT_PATTERN = /^[a-z]{2}$/i;

// ISO-3166-1 alpha-2 country codes (lowercase). Gates market synthesis in
// `getMarketByPathParts`: only a real country code may mint a synthesized market, so
// bogus `/xx/yy` bot-scan paths (invalid codes) resolve to null → 404 instead of
// minting a fresh non-prebuilt market cache key (a first-hit ISR write). ISO-3166 is a
// guaranteed superset of every Shopify market country, so this never blocks a real or
// future market — only junk. (Prebuilt + live-list markets short-circuit before this.)
const ISO_3166_ALPHA2_COUNTRIES: ReadonlySet<string> = new Set(
	'ad ae af ag ai al am ao aq ar as at au aw ax az ba bb bd be bf bg bh bi bj bl bm bn bo bq br bs bt bv bw by bz ca cc cd cf cg ch ci ck cl cm cn co cr cu cv cw cx cy cz de dj dk dm do dz ec ee eg eh er es et fi fj fk fm fo fr ga gb gd ge gf gg gh gi gl gm gn gp gq gr gs gt gu gw gy hk hm hn hr ht hu id ie il im in io iq ir is it je jm jo jp ke kg kh ki km kn kp kr kw ky kz la lb lc li lk lr ls lt lu lv ly ma mc md me mf mg mh mk ml mm mn mo mp mq mr ms mt mu mv mw mx my mz na nc ne nf ng ni nl no np nr nu nz om pa pe pf pg ph pk pl pm pn pr ps pt pw py qa re ro rs ru rw sa sb sc sd se sg sh si sj sk sl sm sn so sr ss st sv sx sy sz tc td tf tg th tj tk tl tm tn to tr tt tv tw tz ua ug um us uy uz va vc ve vg vi vn vu wf ws ye yt za zm zw'.split(
		' ',
	),
);

export const REST_OF_WORLD_MARKET: StorefrontMarket = {
	language: 'en',
	country: 'row',
	languageCode: 'EN',
	countryCode: 'US',
	locale: 'en-001',
	currencyCode: 'USD',
	label: 'Rest of World',
};

export const FALLBACK_MARKETS: StorefrontMarket[] = [
	{
		language: 'en',
		country: 'us',
		languageCode: 'EN',
		countryCode: 'US',
		locale: 'en-US',
		currencyCode: 'USD',
		label: 'United States',
	},
	{
		language: 'en',
		country: 'au',
		languageCode: 'EN',
		countryCode: 'AU',
		locale: 'en-AU',
		currencyCode: 'AUD',
		label: 'Australia',
	},
	{
		language: 'en',
		country: 'ca',
		languageCode: 'EN',
		countryCode: 'CA',
		locale: 'en-CA',
		currencyCode: 'CAD',
		label: 'Canada',
	},
	{
		language: 'en',
		country: 'gb',
		languageCode: 'EN',
		countryCode: 'GB',
		locale: 'en-GB',
		currencyCode: 'GBP',
		label: 'United Kingdom',
	},
	REST_OF_WORLD_MARKET,
];

export const SUPPORTED_MARKETS = FALLBACK_MARKETS;
export const DEFAULT_MARKET = FALLBACK_MARKETS[0];

export function getMarketKey(market: Pick<StorefrontMarket, 'language' | 'country'>) {
	return `${market.language.toLowerCase()}/${market.country.toLowerCase()}`;
}

export function getMarketPathPrefix(market: Pick<StorefrontMarket, 'language' | 'country'>) {
	return `/${getMarketKey(market)}`;
}

export function getMarketSlug(market: Pick<StorefrontMarket, 'language' | 'country'>) {
	return `${market.language.toLowerCase()}-${market.country.toLowerCase()}`;
}

export function getInternalMarketPathPrefix(
	market: Pick<StorefrontMarket, 'language' | 'country'>,
) {
	return `${INTERNAL_MARKET_PATH_PREFIX}/${getMarketSlug(market)}`;
}

export function getMarketCookieValue(market: Pick<StorefrontMarket, 'language' | 'country'>) {
	return getMarketKey(market);
}

export function isRestOfWorldMarket(market: Pick<StorefrontMarket, 'country'>) {
	return market.country.toLowerCase() === REST_OF_WORLD_MARKET.country;
}

export function isUnitedStatesMarket(market: Pick<StorefrontMarket, 'country'>) {
	return market.country.toLowerCase() === DEFAULT_MARKET.country;
}

export function getMarketDisplayFlag(market: Pick<StorefrontMarket, 'country' | 'countryCode'>) {
	return isRestOfWorldMarket(market) ? '🌐' : getMarketFlag(market.countryCode);
}

export function getMarketMoneyLocale(
	market: Pick<StorefrontMarket, 'country'> &
		Partial<Pick<StorefrontMarket, 'currencyCode' | 'locale'>>,
) {
	if (isRestOfWorldMarket(market)) return REST_OF_WORLD_MARKET.locale;

	const locale = market.locale ?? DEFAULT_MARKET.locale;
	const currencyCode = market.currencyCode?.toUpperCase();
	if (
		currencyCode &&
		currencyCode !== DEFAULT_MARKET.currencyCode &&
		currencyUsesBareDollarSymbol(locale, currencyCode)
	) {
		return REST_OF_WORLD_MARKET.locale;
	}

	return locale;
}

function currencyUsesBareDollarSymbol(locale: string, currencyCode: string) {
	try {
		return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode })
			.formatToParts(1)
			.some((part) => part.type === 'currency' && part.value === '$');
	} catch {
		return false;
	}
}

export function getMarketFlag(countryCode: string) {
	const normalizedCountry = countryCode.trim().toUpperCase();
	if (!/^[A-Z]{2}$/.test(normalizedCountry)) return '🏳';

	return Array.from(normalizedCountry)
		.map((char) => String.fromCodePoint(127_397 + char.charCodeAt(0)))
		.join('');
}

export function getMarketByCountry(
	country: string | null | undefined,
	markets: StorefrontMarket[] = FALLBACK_MARKETS,
) {
	if (!country) return null;
	const normalizedCountry = country.toLowerCase();
	return markets.find((market) => market.country === normalizedCountry) ?? null;
}

export function getMarketByPathParts(
	language: string | undefined,
	country: string | undefined,
	markets: StorefrontMarket[] = FALLBACK_MARKETS,
) {
	if (!language || !country) return null;
	const normalizedLanguage = language.toLowerCase();
	const normalizedCountry = country.toLowerCase();
	const supportedMarket = markets.find(
		(market) => market.language === normalizedLanguage && market.country === normalizedCountry,
	);
	if (supportedMarket) return supportedMarket;
	if (!isMarketPathPair(normalizedLanguage, normalizedCountry)) return null;
	// Only a real ISO-3166 country code may synthesize a market. Without this, any
	// two-letter pair (`/xx/yy` bot scans) mints a fresh non-prebuilt market cache key
	// = a first-hit ISR write. ISO-3166 is a superset of every Shopify market country,
	// so a real/future market is never blocked here — it would already have matched the
	// prebuilt/live-list `find` above, or be a valid ISO code synthesized below.
	if (!ISO_3166_ALPHA2_COUNTRIES.has(normalizedCountry)) return null;

	const countryMarket = getMarketByCountry(normalizedCountry, markets);
	return {
		...(countryMarket ?? DEFAULT_MARKET),
		language: normalizedLanguage,
		country: normalizedCountry,
		languageCode: normalizedLanguage.toUpperCase(),
		countryCode: normalizedCountry.toUpperCase(),
		locale: `${normalizedLanguage}-${normalizedCountry.toUpperCase()}`,
	};
}

export function getMarketFromValue(
	value: string | null | undefined,
	markets: StorefrontMarket[] = FALLBACK_MARKETS,
) {
	if (!value) return null;
	const decodedValue = decodeMarketValue(value);
	const [language, country] = decodedValue.replace('-', '/').split('/');
	return getMarketByPathParts(language, country, markets);
}

export function getMarketFromSlug(
	slug: string | null | undefined,
	markets: StorefrontMarket[] = FALLBACK_MARKETS,
) {
	if (!slug) return null;
	const decodedSlug = decodeMarketValue(slug);
	const [language, country] = decodedSlug.replace('-', '/').split('/');
	return getMarketByPathParts(language, country, markets);
}

function decodeMarketValue(value: string) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

export function getMarketFromPathname(pathname: string) {
	const [, language, country] = pathname.split('/');
	// Shopify's former storefront used /en-au; the headless router also
	// accepts /en/au. Both must resolve before rewriting to internal routes.
	if (/^[a-z]{2}-[a-z]{2}$/i.test(language ?? '')) {
		return getMarketFromSlug(language);
	}
	return getMarketByPathParts(language, country);
}

export function isMarketPathPair(language: string | undefined, country: string | undefined) {
	return Boolean(
		language &&
			country &&
			MARKET_SEGMENT_PATTERN.test(language) &&
			MARKET_SEGMENT_PATTERN.test(country),
	);
}

export function stripMarketPrefix(pathname: string) {
	const market = getMarketFromPathname(pathname);
	if (!market) return pathname || '/';

	const firstSegment = pathname.split('/')[1];
	const prefix = /^[a-z]{2}-[a-z]{2}$/i.test(firstSegment ?? '')
		? `/${firstSegment}`
		: getMarketPathPrefix(market);
	const stripped = pathname.slice(prefix.length);
	return stripped || '/';
}

export function getInternalMarketFromPathname(
	pathname: string,
	markets: StorefrontMarket[] = FALLBACK_MARKETS,
) {
	const [, prefix, marketSlug] = pathname.split('/');
	if (`/${prefix}` !== INTERNAL_MARKET_PATH_PREFIX) return null;
	return getMarketFromSlug(marketSlug, markets);
}

export function isInternalMarketPath(pathname: string) {
	return (
		pathname === INTERNAL_MARKET_PATH_PREFIX ||
		pathname.startsWith(`${INTERNAL_MARKET_PATH_PREFIX}/`)
	);
}

export function stripInternalMarketPrefix(pathname: string) {
	const market = getInternalMarketFromPathname(pathname);
	if (!market) return pathname || '/';

	const prefix = getInternalMarketPathPrefix(market);
	const stripped = pathname.slice(prefix.length);
	return stripped || '/';
}

export function toCanonicalPath(pathname: string) {
	return stripMarketPrefix(stripInternalMarketPrefix(pathname));
}

export function buildInternalMarketPath(
	pathname: string,
	market: Pick<StorefrontMarket, 'language' | 'country'>,
) {
	const canonicalPath = toCanonicalPath(pathname);
	return `${getInternalMarketPathPrefix(market)}${canonicalPath === '/' ? '' : canonicalPath}`;
}

export const INTERNAL_MARKET_DRAFT_PATH_PREFIX = '/storefront-market-draft';

/**
 * Internal rewrite target for draft-mode (live-preview) requests. Mirrors
 * `buildInternalMarketPath` but routes into the `storefront-market-draft`
 * segment, which renders dynamically with the Payload live-preview client.
 * Editors reach it via the proxy when the draft cookie is present; the visible
 * URL stays canonical, so the public `storefront-market` segment can prerender
 * statically (no `draftMode()` in its render path). The `-draft` prefix is not
 * `isInternalMarketPath`, so direct public access 404s rather than resolving.
 */
export function buildInternalDraftMarketPath(
	pathname: string,
	market: Pick<StorefrontMarket, 'language' | 'country'>,
) {
	const canonicalPath = toCanonicalPath(pathname);
	return `${INTERNAL_MARKET_DRAFT_PATH_PREFIX}/${getMarketSlug(market)}${canonicalPath === '/' ? '' : canonicalPath}`;
}

export function localizePath(
	pathname: string,
	market: Pick<StorefrontMarket, 'language' | 'country'>,
) {
	if (!pathname.startsWith('/')) return pathname;

	const cleanPath = toCanonicalPath(pathname);
	return `${getMarketPathPrefix(market)}${cleanPath === '/' ? '' : cleanPath}`;
}

export function getCurrentMarketPrefix() {
	if (typeof window === 'undefined') return '';
	const market = getMarketFromPathname(window.location.pathname);
	return market ? getMarketPathPrefix(market) : '';
}

export function localizePathForCurrentMarket(pathname: string) {
	if (typeof window !== 'undefined' && isApiPath(pathname)) {
		const marketKey = getCurrentBrowserMarketKey();
		return marketKey ? appendMarketQueryParam(pathname, marketKey) : pathname;
	}

	return pathname;
}

function isApiPath(pathname: string) {
	return pathname === '/api' || pathname.startsWith('/api/') || pathname.startsWith('/api?');
}

export function getCurrentBrowserMarketKey() {
	const pathMarket = getMarketFromPathname(window.location.pathname);
	if (pathMarket) return getMarketKey(pathMarket);

	const cookieValue = getBrowserCookieValue(MARKET_COOKIE_NAME);
	const cookieMarket = getMarketFromValue(cookieValue);
	return cookieMarket ? getMarketKey(cookieMarket) : null;
}

export function getBrowserCookieValue(name: string) {
	if (typeof document === 'undefined') return undefined;

	return document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${name}=`))
		?.split('=')[1];
}

export function setBrowserMarketCookie(market: Pick<StorefrontMarket, 'language' | 'country'>) {
	if (typeof window === 'undefined') return;

	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	// biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API support is uneven; the next request reads this server-side.
	document.cookie = `${MARKET_COOKIE_NAME}=${getMarketCookieValue(market)}; path=/; max-age=${MARKET_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function appendMarketQueryParam(pathname: string, marketKey: string) {
	const [withoutHash, hash] = pathname.split('#', 2);
	const [path, search] = withoutHash.split('?', 2);
	const params = new URLSearchParams(search ?? '');

	if (!params.has(MARKET_QUERY_PARAM)) {
		params.set(MARKET_QUERY_PARAM, marketKey);
	}

	const query = params.toString();
	return `${path}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
}
